import type { NavigateFunction } from 'react-router-dom';
import type { Vendor } from '@shopgnd/shared';
import {
  computeVendorSeoScoreCore,
  VENDOR_SEO_SUBMIT_MIN_SCORE,
} from '@shopgnd/shared';
import type { PlanId } from '@/lib/vendorPlans';
import {
  clearOptionalProSignupGoal,
  releaseForcedPostSignupRedirects,
} from '@/lib/postSignupNavigation';
import {
  vendorAwaitingAdminReview,
  vendorIsApproved,
  vendorIsRejected,
} from '@/lib/vendorApproval';

const SKIPPED_STEPS_KEY = 'shopgnd-vendor-onboard-skipped';

export type VendorOnboardingForm = {
  plan: PlanId;
  businessName: string;
  description: string;
  directorySector: string;
  directoryCategoryId: string;
  businessType: string;
  parish: string;
  phone: string;
  whatsapp: string;
  website: string;
  instagramUrl: string;
  facebookUrl: string;
  tiktokUrl: string;
  logoUrl: string;
  slug: string;
  pickupParishes: string[];
  offersDelivery: boolean;
  offersCod: boolean;
  offersCard: boolean;
  isRegisteredBusiness: boolean;
  businessRegistrationNumber: string;
  taxId: string;
  registrationDocumentKey: string;
};

export function markOnboardingStepSkipped(stepLabel: string): void {
  try {
    const steps = getSkippedOnboardingSteps();
    if (!steps.includes(stepLabel)) steps.push(stepLabel);
    sessionStorage.setItem(SKIPPED_STEPS_KEY, JSON.stringify(steps));
  } catch {
    /* private mode */
  }
}

export function getSkippedOnboardingSteps(): string[] {
  try {
    const raw = sessionStorage.getItem(SKIPPED_STEPS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

export function clearOnboardingSkippedSteps(): void {
  try {
    sessionStorage.removeItem(SKIPPED_STEPS_KEY);
  } catch {
    /* ignore */
  }
}

export function validateVendorOnboardingSubmit(params: {
  data: VendorOnboardingForm;
  agreementReady: boolean;
  registrationFile: File | null;
  existingRegistrationKey?: string | null;
  needsPayment: boolean;
  cardSaved: boolean;
  submitFromPaymentStep: boolean;
  trustedPartner?: boolean;
}): { ok: true } | { ok: false; message: string } {
  const {
    data,
    agreementReady,
    registrationFile,
    existingRegistrationKey,
    needsPayment,
    cardSaved,
    submitFromPaymentStep,
    trustedPartner,
  } = params;

  if (data.businessName.trim().length < 2) {
    return { ok: false, message: 'Enter your business name (at least 2 characters).' };
  }
  if (!data.parish) {
    return { ok: false, message: 'Select your business location.' };
  }
  if (
    data.isRegisteredBusiness &&
    !registrationFile &&
    !existingRegistrationKey?.trim() &&
    !data.registrationDocumentKey.trim()
  ) {
    return { ok: false, message: 'Upload your business registration certificate.' };
  }
  if (data.slug.trim().length < 2) {
    return { ok: false, message: 'Choose a store URL (at least 2 characters) on the Store step.' };
  }
  if (!data.logoUrl.trim()) {
    return { ok: false, message: 'Add a store logo on the Store step before submitting.' };
  }
  if (!agreementReady) {
    return { ok: false, message: 'Accept all required agreements before submitting.' };
  }
  if (needsPayment && submitFromPaymentStep && !cardSaved) {
    return { ok: false, message: 'Save a payment method for your plan before submitting.' };
  }
  if (!trustedPartner) {
    const score = computeVendorSeoScoreCore({
      description: data.description,
      pickupParish: data.parish,
      logoUrl: data.logoUrl,
      phone: data.phone || data.whatsapp,
    });
    if (score < VENDOR_SEO_SUBMIT_MIN_SCORE) {
      return {
        ok: false,
        message:
          'Complete your store description (mention Grenada and your parish, list what you offer), add a phone number, and upload a logo before submitting.',
      };
    }
  }
  return { ok: true };
}

/** Profile fields still missing after onboarding (for hub / done-step nudges). */
export function vendorProfileCompletionGaps(
  vendor: Pick<
    Vendor,
    | 'logoUrl'
    | 'businessName'
    | 'phone'
    | 'pickupParish'
    | 'description'
    | 'slug'
    | 'registrationDocumentKey'
    | 'isRegisteredBusiness'
  >,
): string[] {
  const gaps: string[] = [];
  if (!vendor.businessName?.trim()) gaps.push('business name');
  if (!vendor.logoUrl?.trim()) gaps.push('store logo');
  if (!vendor.pickupParish?.trim()) gaps.push('business location');
  if (!vendor.phone?.trim()) gaps.push('contact phone');
  if (!vendor.slug?.trim()) gaps.push('store URL');
  if (vendor.isRegisteredBusiness && !vendor.registrationDocumentKey?.trim()) {
    gaps.push('registration certificate');
  }
  return gaps;
}

export function vendorOnboardingFormGaps(data: VendorOnboardingForm): string[] {
  const gaps: string[] = [];
  if (data.businessName.trim().length < 2) gaps.push('business name');
  if (!data.logoUrl.trim()) gaps.push('store logo');
  if (!data.parish) gaps.push('business location');
  if (data.slug.trim().length < 2) gaps.push('store URL');
  if (data.isRegisteredBusiness && !data.registrationDocumentKey.trim()) {
    gaps.push('registration certificate');
  }
  return gaps;
}

/** Status after seller submits / resubmits onboarding.
 * New stores auto-approve (same as Find a Pro). Rejected stores return to pending for admin re-review.
 */
export function approvalStatusAfterOnboardingSubmit(
  previousStatus: Vendor['approvalStatus'] | undefined,
  _trustedPartner?: boolean,
): Vendor['approvalStatus'] {
  if (vendorIsApproved(previousStatus)) return 'approved';
  if (vendorIsRejected(previousStatus)) return 'pending';
  return 'approved';
}

/**
 * Send application-received email only on first submission or rejected → pending resubmit.
 * Skip when already awaiting review (pending) to avoid 400s from vendor-notify.
 */
export function shouldNotifyVendorApplicationSubmitted(
  previousStatus: Vendor['approvalStatus'] | undefined,
): boolean {
  if (vendorIsRejected(previousStatus)) return true;
  return (previousStatus ?? 'none') === 'none';
}

/** After seller onboarding submit — hub if already approved, otherwise marketplace. */
export function navigateToMarketplaceAfterSellerApplication(
  navigate: NavigateFunction,
  vendor: Vendor | null,
  businessName: string,
): void {
  releaseForcedPostSignupRedirects();
  if (!vendor || !vendorIsApproved(vendor.approvalStatus)) {
    clearOptionalProSignupGoal();
  }
  if (vendor && vendorIsApproved(vendor.approvalStatus)) {
    navigate('/hub/home', { replace: true });
    return;
  }
  const name = vendor?.businessName?.trim() || businessName.trim() || 'your store';
  const rejected = vendor ? vendorIsRejected(vendor.approvalStatus) : false;
  navigate('/grenada/shop', {
    replace: true,
    state: {
      sellerPending: true,
      sellerRejected: rejected,
      message: rejected
        ? `Your store application for ${name} was not approved. Check your email for details.`
        : `Thanks — your application for ${name} is under review. Keep shopping on ShopGND; we'll email you when your store is approved.`,
    },
  });
}

export function shouldReturnPendingSellerToMarketplace(
  status: Vendor['approvalStatus'] | undefined,
): boolean {
  return vendorAwaitingAdminReview(status) || vendorIsRejected(status);
}
